import { useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { FilterSection } from '../../components/EligibilityRules/FilterSection';
import { FilterResetButton } from '../../components/EligibilityRules/FilterResetButton';
import { SecuritySearchPopover } from '../../components/EligibilityRules/SecurityTypeClassification/SecuritySearchPopover';
import { useStartingUniverseStore } from '../../store/useStartingUniverseStore';
import type { Security } from '@biasafe.models';
import type { useEsgMandateScreens } from '../../hooks';
import { SelectionBadge } from '@/features/Strategies/components/SelectionBadge';
import { KeywordBasedExclusions } from './KeywordBasedExclusions';

interface EsgMandateScreensProps {
  esgMandate: ReturnType<typeof useEsgMandateScreens>;
}

// Section structure mirrors figma UniverseSelection.tsx 5249-5982:
//
//   ESG & Mandate Screens (outer wrapper)
//     ├─ Single Security Exclusions     ← figma 5435
//     │    ├─ Exclusion List            ← figma 5468
//     │    └─ Keyword-Based Exclusions  ← figma 5812 — Contains/Match toggles
//     └─ Environmental, Social, & Governance (ESG)       ← figma 5266 — static "Coming Soon"
//          header only (restriction-list screening not wired yet)
//
// Inclusion List is intentionally NOT rendered — figma's design has
// only the Exclusion List variant in this surface (the Inclusion
// counterpart lives elsewhere in the wizard).
//
// Environmental, Social, & Governance (ESG) is a non-collapsible placeholder: the
// restriction-list screener (Financial / Regulatory / ESG & Value-based
// tabs) is removed until the backend submission contract gains a field
// for sanctioned categories. Keyword-Based Exclusions holds its state
// LOCALLY for the same reason — plumbing it into ``data_processor`` would
// silently drop on the server side. When those fields land, the section
// grows a body back and Keyword swaps from useState to store selectors.
export function EsgMandateScreens({ esgMandate }: EsgMandateScreensProps) {
  // FilterSection rows are collapsible: `open` reads the store's per-section
  // expanded state (flipped by onToggle); they default open on first mount.
  const toggleSection = useStartingUniverseStore((s) => s.toggleFilterSection);
  const initSection = useStartingUniverseStore((s) => s.initFilterSection);
  const expandedSections = useStartingUniverseStore(
    (s) => s.expandedFilterSectionIds,
  );

  // Seed default open state on first mount.
  useEffect(() => {
    initSection('esg_mandate_screens_outer', false);
    initSection('esg_compliance_policy', true);
    initSection('esg_security_restrictions', true);
  }, [initSection]);

  const {
    blockedList,
    availableSecurities,
    blockedSecurities,
    addToBlocked,
    removeFromBlocked,
    esgExcludedCount,
  } = esgMandate;

  const excludedCount = blockedList.length;

  const resetExcluded = () => {
    blockedSecurities.forEach((s: Security) =>
      removeFromBlocked(s.performanceId ?? s.ticker),
    );
  };

  return (
    <div className="space-y-2.5">
      <FilterSection
        title="Policy & Mandate Screens"
        excludedCount={excludedCount + esgExcludedCount}
        open={expandedSections['esg_mandate_screens_outer'] ?? false}
        onToggle={() => toggleSection('esg_mandate_screens_outer')}
      >
        <div className="space-y-2.5">
          {/* ── Single Security Exclusions (figma 5435) — only the
              Exclusion variant per the design owner's preferred
              layout; the Inclusion list is intentionally omitted. */}
          <FilterSection
            title="Single Security Exclusions"
            excludedCount={excludedCount}
            open={expandedSections['esg_security_restrictions'] ?? true}
            onToggle={() => toggleSection('esg_security_restrictions')}
          >
            <div className="space-y-6">
              {/* Exclusion List — figma 5465-5805. Label + inline Reset (the
                  "{N} excluded" count lives on the section header only, per
                  figma 5440-5491), the combobox, then the blocked chips in a
                  muted card. */}
              <div className="space-y-2.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-muted-foreground text-[15px]">
                      Exclusion List
                    </span>
                    {excludedCount > 0 && (
                      <FilterResetButton onClick={resetExcluded} />
                    )}
                  </div>
                </div>
                <SecuritySearchPopover
                  variant="blocked"
                  items={availableSecurities}
                  onSelect={addToBlocked}
                />
                {blockedSecurities.length > 0 && (
                  // Figma 5705-5749: blocked chips sit in a muted card with a
                  // small "Exclusion List" sub-heading, wrapping horizontally.
                  <div className="flex flex-col gap-3 bg-muted/30 rounded-md border border-border/50 px-[12px] py-[10px]">
                    <div className="space-y-1.5 pt-1">
                      <div className="font-semibold text-muted-foreground text-[12px]">
                        Exclusion List
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {blockedSecurities.map((security: Security, index: number) => (
                          <SelectionBadge
                            key={`${security.ticker}-${index}`}
                            intent="destructive"
                            item={security}
                            getCode={(s: Security) => s.ticker}
                            getLabel={(s: Security) => s.name}
                            onRemove={(s: Security) =>
                              removeFromBlocked(s.performanceId ?? s.ticker)
                            }
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Keyword-Based Exclusions — figma 5812. */}
              <KeywordBasedExclusions />
            </div>
          </FilterSection>

          {/* ── Environmental, Social, & Governance (ESG) (figma 5942) ───────────────
              Restriction-list screening isn't wired to the engine yet, so
              this is a static "Coming Soon" header only — no expand chevron
              and no body until the sanctioned-category contract lands. */}
          <FilterSection
            title="Environmental, Social, & Governance (ESG)"
            collapsible={false}
            badge={
              <Badge
                variant="outline"
                className="px-2 py-0.5 bg-amber-500/10 text-amber-600 border-amber-500/30 font-medium text-[12px]"
              >
                Coming Soon
              </Badge>
            }
          />
        </div>
      </FilterSection>
    </div>
  );
}
